/**
 * Account Service
 * Handles API calls for account operations
 * Task 003 - Account Management
 * Task 003c - Account Closing & Deletion Flow
 */

import { 
  Account, 
  CreateAccountData, 
  UpdateAccountData, 
  AccountCloseResponse,
  AccountTransactionCount 
} from '../types/account';
import apiClient from '../lib/apiClient';

/**
 * Create a new account
 */
export const createAccount = async (data: CreateAccountData): Promise<Account> => {
  return apiClient.post('/accounts', data);
};

/**
 * Get all accounts for the current user (no month_plan filtering)
 */
export const getUserAccounts = async (status?: 'active' | 'closed'): Promise<Account[]> => {
  const queryString = status ? `?status=${status}` : '';
  return apiClient.get(`/accounts${queryString}`);
};

/**
 * Get a specific account by ID
 */
export const getAccountById = async (accountId: string): Promise<Account> => {
  return apiClient.get(`/accounts/${accountId}`);
};

/**
 * Update an account
 */
export const updateAccount = async (
  accountId: string,
  data: UpdateAccountData,
  monthPlanId?: string
): Promise<Account> => {
  const queryString = monthPlanId ? `?month_plan_id=${monthPlanId}` : '';
  return apiClient.put(`/accounts/${accountId}${queryString}`, data);
};

/**
 * Delete an account
 */
export const deleteAccount = async (accountId: string): Promise<{ message: string }> => {
  return apiClient.delete(`/accounts/${accountId}`);
};

/**
 * Close an account (creates zeroing transaction and sets status to closed)
 */
export const closeAccount = async (accountId: string, monthPlanId: string): Promise<AccountCloseResponse> => {
  return apiClient.post(`/accounts/${accountId}/close?month_plan_id=${monthPlanId}`);
};

/**
 * Reopen a closed account (sets status back to active)
 */
export const reopenAccount = async (accountId: string): Promise<Account> => {
  return apiClient.post(`/accounts/${accountId}/reopen`);
};

/**
 * Get transaction count for an account
 */
export const getTransactionCount = async (accountId: string): Promise<AccountTransactionCount> => {
  return apiClient.get(`/accounts/${accountId}/transaction-count`);
};
